<template>
  <UIButton
    type="button"
    :label
    :disabled
    :loading="disabled"
    :trailing
    variant="secondary"
    fill="light"
    @click="handleClick"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { NOTIFICATION_DELAY } from '@/const'

import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'
import { useResponsive } from '@/plugins/responsiveUI'

import { UIButton } from '@ui/buttons'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'

type Props = {
  repositoryId: string
}
const props = defineProps<Props>()

const { success } = useNotifications()

const { isMobile } = useResponsive()

const analyticsStore = useAnalyticsStore()
const repositoriesStore = useRepositoriesStore()

const disabled = ref(false)

const label = computed(() =>
  isMobile.value ? undefined : 'Clear analytics cache',
)

const trailing = computed(() => (isMobile.value ? ArrowPathIcon : undefined))

const handleClick = async () => {
  try {
    disabled.value = true
    await repositoriesStore.clearCache(props.repositoryId)
    await analyticsStore.markAsDeprecated()
    await success(
      {
        message: 'Analytics cache cleared',
      },
      NOTIFICATION_DELAY,
    )
  } catch (e) {
    handleCatchedError(e as string, { repositoryId: props.repositoryId })
  } finally {
    disabled.value = false
  }
}
</script>
